/**
 * YouTube link parsing and embed URL building for the floating livestream
 * player (issues #50-#57).
 *
 * Deliberately dependency-free and React-free so it can be unit tested on its own.
 *
 * Two shapes of link reach us from a `youtubeLiveEvent`'s `meetingLink`, and both
 * have to work:
 *
 *   - a VIDEO link (`/watch?v=…`, `youtu.be/…`, `/live/…`) — used when someone
 *     pastes the actual broadcast URL for a one-off stream;
 *   - a CHANNEL link (`/@handle`, `/channel/UC…`) — used for an ongoing channel,
 *     since the same event can't carry a per-broadcast URL ahead of time.
 *
 * A channel is never embedded directly. It is resolved to whatever it is
 * broadcasting right now (see lib/livestream/youtubeLive.ts) and THAT video is
 * embedded. A video link is always preferred when present: it addresses one
 * specific broadcast, so it cannot resolve to the wrong thing.
 *
 * This is a separate module from lib/youtube.ts (which holds getYouTubeEmbedUrl
 * for the manual widget from issues #43-#49) — the two widgets are independent
 * and kept in parallel per product decision.
 *
 * Adapted from tradex-platform (branch feat/livestream-pip), lib/youtube.ts —
 * same logic, no behavior changes.
 */

/** YouTube video ids are exactly 11 characters of the URL-safe base64 alphabet. */
const VIDEO_ID_RE = /^[A-Za-z0-9_-]{11}$/;

/** Channel ids are always `UC` followed by 22 URL-safe base64 characters. */
const CHANNEL_ID_RE = /^UC[A-Za-z0-9_-]{22}$/;

/**
 * Handles are `@` plus 3–30 of letters, digits, underscore, hyphen and period —
 * YouTube's own rules. Anything outside this never reaches a URL.
 */
const HANDLE_RE = /^@[A-Za-z0-9_.-]{3,30}$/;

/**
 * Hosts we are willing to embed.
 *
 * The link this parses comes from Sanity, typed by hand by an editor, and what
 * it produces ends up in a fixed-position iframe layered over the homepage. An
 * allow-list is the point of this function — a regex over the raw string would
 * happily accept `https://evil.example/?v=dQw4w9WgXcQ`.
 */
const ALLOWED_HOSTS = new Set([
  'youtube.com',
  'www.youtube.com',
  'm.youtube.com',
  'youtu.be',
  'www.youtu.be',
  'youtube-nocookie.com',
  'www.youtube-nocookie.com',
]);

export type YouTubeSource =
  /** One specific broadcast. */
  | { kind: 'video'; videoId: string }
  /**
   * A channel, identified the way the editor wrote it: an `@handle` or a `UC…`
   * id. Both address `youtube.com/<channel>/live` equally well, so there is no
   * translation table to maintain — a new channel works the moment its link is
   * pasted into Sanity.
   */
  | { kind: 'channel'; channel: string };

/**
 * Stable identifier for a source — the iframe key, the analytics id, and the
 * preview marker all use it.
 *
 * It round-trips through `parseSourceKey`, which is what lets consumers hold a
 * plain string and rebuild the object with a stable identity. That matters:
 * the schedule is re-polled periodically, so the event objects (and any source
 * derived from them) get fresh identities on every poll, which would otherwise
 * re-commit the iframe `src` and restart the stream.
 */
export function sourceKey(source: YouTubeSource): string {
  return source.kind === 'video' ? source.videoId : `live:${source.channel}`;
}

/** Inverse of {@link sourceKey}. */
export function parseSourceKey(key: string): YouTubeSource | null {
  if (key.startsWith('live:')) {
    const channel = key.slice(5);
    return isValidChannel(channel) ? { kind: 'channel', channel } : null;
  }
  return isValidVideoId(key) ? { kind: 'video', videoId: key } : null;
}

export function isValidVideoId(id?: string | null): id is string {
  return !!id && VIDEO_ID_RE.test(id);
}

/** An `@handle` or a `UC…` id — the two things that can go in `youtube.com/<here>/live`. */
export function isValidChannel(channel?: string | null): channel is string {
  return !!channel && (HANDLE_RE.test(channel) || CHANNEL_ID_RE.test(channel));
}

/** The path segment that addresses this channel: `@handle` or `channel/UC…`. */
export function channelPath(channel: string): string {
  return channel.startsWith('@') ? channel : `channel/${channel}`;
}

/**
 * What `link` points at, or `null` if it isn't something we trust enough to embed.
 *
 * Handles the share formats people actually paste:
 *   youtube.com/watch?v=ID    youtu.be/ID       youtube.com/live/ID
 *   youtube.com/embed/ID      youtube.com/shorts/ID
 *   youtube.com/@handle       youtube.com/channel/UC…    (both with an optional /live)
 *
 * `/live/ID` matters most among the video forms: that is what the share button
 * gives you while a broadcast is actually running, which is exactly when someone
 * fills this field in.
 */
export function parseYouTubeSource(link?: string | null): YouTubeSource | null {
  if (!link) return null;

  let url: URL;
  try {
    url = new URL(link);
  } catch {
    return null;
  }

  if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
  if (!ALLOWED_HOSTS.has(url.hostname)) return null;

  const segments = url.pathname.split('/').filter(Boolean);
  const first = segments[0] ?? '';

  // ── Video forms ──
  let videoCandidate: string | null = null;
  if (url.hostname === 'youtu.be' || url.hostname === 'www.youtu.be') {
    videoCandidate = segments[0] ?? null;
  } else if (url.pathname === '/watch') {
    videoCandidate = url.searchParams.get('v');
  } else if (first === 'live' || first === 'embed' || first === 'shorts') {
    videoCandidate = segments[1] ?? null;
  }
  if (isValidVideoId(videoCandidate)) return { kind: 'video', videoId: videoCandidate };

  // ── Channel forms ──
  if (first === 'channel' && isValidChannel(segments[1])) {
    return { kind: 'channel', channel: segments[1] };
  }
  if (isValidChannel(first)) {
    return { kind: 'channel', channel: first };
  }

  return null;
}

/**
 * The embed URL for the floating player. Always a specific video — a channel
 * source has to be resolved to its live video first.
 *
 * - `mute=1` is not optional: every browser blocks unmuted autoplay, and muted
 *   autoplay is unconditionally allowed. `autoplay=1` on its own does nothing.
 * - `playsinline=1` is what makes this work on iOS. Without it WebKit hands the
 *   video to the native fullscreen player, which needs a user gesture, so playback
 *   never starts — the classic "fine on desktop, dead on iPhone" failure.
 * - `rel=0` no longer removes related videos (since 2018 it only restricts them to
 *   the same channel). Kept because same-channel is still an improvement.
 * - `modestbranding` is intentionally absent — YouTube stopped honouring it in 2023.
 *
 * - `enablejsapi=1` + `origin` let the page drive the player over postMessage
 *   (see FloatingLivestreamCard's `mute`/`playVideo` on ready). Mobile browsers
 *   often ignore the `autoplay` param alone, but accept a muted `playVideo()`.
 *
 * `youtube-nocookie.com` is the privacy-enhanced host. It reduces, but does not
 * remove, what is sent to Google before the user consents.
 */
export const YOUTUBE_EMBED_ORIGIN = 'https://www.youtube-nocookie.com';

export function buildYouTubeEmbedUrl(videoId: string, origin?: string): string {
  const params = new URLSearchParams({
    autoplay: '1',
    mute: '1',
    playsinline: '1',
    rel: '0',
    enablejsapi: '1',
  });
  if (origin) params.set('origin', origin);
  return `${YOUTUBE_EMBED_ORIGIN}/embed/${videoId}?${params.toString()}`;
}

/**
 * YouTube's periodically refreshed frame of a running broadcast — the
 * thumbnail in the desktop live pop-up (FloatingLivestreamCard).
 */
export function buildYouTubeLiveThumbnailUrl(videoId: string): string {
  return `https://i.ytimg.com/vi/${videoId}/hqdefault_live.jpg`;
}

/** Public URL, used for the "watch on YouTube" escape link in the card header. */
export function buildYouTubeWatchUrl(source: YouTubeSource): string {
  return source.kind === 'video'
    ? `https://www.youtube.com/watch?v=${source.videoId}`
    : `https://www.youtube.com/${channelPath(source.channel)}/live`;
}
